<template>
  <div class="question-row">
    <div class="row">
      <div class="col-12 col-lg-4 question-row__title">
        {{ question.name }}
      </div>
      <div class="col-12 col-lg-8">
        <validation-provider
          ref="answersProvider"
          v-slot="{ errors }"
          :name="`question-${question.id}`"
          rules="required_structured_answers"
          :vid="`question-${question.id}`"
        >
          <div class="row">
            <div
              v-for="(answer, index) in mainAnswers"
              :key="answer.id"
              class="col-6 col-md-4 col-xl mb-2"
              :class="{
                'answer-col--with-divider': index === 1,
              }"
            >
              <div
                class="answer-card"
                :class="{
                  'answer-card--selected':
                    model.selectedMainAnswerId === answer.id && !isReadOnly,
                  'answer-card--disabled-selected':
                    model.selectedMainAnswerId === answer.id && isReadOnly,
                  'answer-card--readonly': isReadOnly,
                }"
                @click="selectAnswer(answer)"
              >
                <div
                  class="answer-card__radio-circle"
                  :class="{
                    'answer-card__radio-circle--selected':
                      model.selectedMainAnswerId === answer.id && !isReadOnly,
                    'answer-card__radio-circle-disabled--selected':
                      model.selectedMainAnswerId === answer.id && isReadOnly,
                  }"
                >
                  <i
                    v-if="model.selectedMainAnswerId === answer.id"
                    class="fas fa-check answer-card__check"
                  />
                </div>
                <div class="vr" />
                <div class="answer-card__label">
                  {{ answer.name }}
                </div>
              </div>
            </div>
            <div
              v-for="(answer, index) in supportAnswers"
              :key="answer.id"
              class="col-6 col-md-4 col-xl mb-2"
              :class="{
                'answer-col--with-divider': index === 2,
              }"
            >
              <div
                class="answer-card"
                :class="{
                  'answer-card--selected':
                    model.selectedSupportAnswerId === answer.id && !isReadOnly,
                  'answer-card--disabled-selected':
                    model.selectedSupportAnswerId === answer.id && isReadOnly,
                  'answer-card--readonly': isReadOnly,
                }"
                @click="selectAnswer(answer)"
              >
                <div
                  class="answer-card__radio-circle"
                  :class="{
                    'answer-card__radio-circle--selected':
                      model.selectedSupportAnswerId === answer.id &&
                      !isReadOnly,
                    'answer-card__radio-circle-disabled--selected':
                      model.selectedSupportAnswerId === answer.id && isReadOnly,
                  }"
                >
                  <i
                    v-if="model.selectedSupportAnswerId === answer.id"
                    class="fas fa-check answer-card__check"
                  />
                </div>
                <div class="vr" />
                <div class="answer-card__label">
                  {{ answer.name }}
                </div>
              </div>
            </div>
          </div>
          <input type="hidden" :value="questionValidationValue" />

          <div v-if="errors[0]" class="font-14 mt-1 error-message">
            {{ errors[0] }}
          </div>
        </validation-provider>

        <div class="question-row__observation-label">
          Observaciones:
        </div>

        <validation-provider
          v-slot="{ errors }"
          :name="`observation-${question.id}`"
          rules="required"
          :vid="`observation-${question.id}`"
        >
          <textarea
            class="form-control question-row__observation"
            :disabled="isReadOnly"
            maxlength="500"
            rows="2"
            :value="model.observation"
            @input="updateObservation($event.target.value)"
          />
          <div class="error-message font-14 mt-1">
            {{ errors[0] }}
          </div>
        </validation-provider>
      </div>
    </div>
  </div>
</template>

<script>
import { extend } from 'vee-validate';
extend('required_structured_answers', {
  validate(value) {
    if (!value) return false;

    const [main, support] = String(value).split('|');

    if (support == QuestionAnswerTypes.NA) {
      return true;
    }

    return !!main && !!support;
  },
  message: 'Debe seleccionar una opción en ambos grupos.',
});

const QuestionAnswerTypes = {
  Si: 1,
  No: 2,
  ConApoyo: 3,
  SinApoyo: 4,
  Acomodo: 5,
  NA: 7,
};
export default {
  name: 'SkillQuestionRow',
  props: {
    question: {
      type: Object,
      required: true,
    },
    answers: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    model() {
      return {
        selectedMainAnswerId: this.question?.selectedMainAnswerId ?? null,
        selectedSupportAnswerId: this.question?.selectedSupportAnswerId ?? null,
        observation: this.question?.observation ?? null,
      };
    },
    questionValidationValue() {
      return `${this.model.selectedMainAnswerId || ''}|${this.model
        .selectedSupportAnswerId || ''}`;
    },
    isValid() {
      return (
        this.model.selectedSupportAnswerId == QuestionAnswerTypes.NA ||
        (this.model.selectedMainAnswerId && this.model.selectedSupportAnswerId)
      );
    },
    mainAnswers() {
      return this.answers.filter((a) =>
        [QuestionAnswerTypes.Si, QuestionAnswerTypes.No].includes(a.id)
      );
    },
    supportAnswers() {
      return this.answers.filter((a) =>
        [
          QuestionAnswerTypes.ConApoyo,
          QuestionAnswerTypes.SinApoyo,
          QuestionAnswerTypes.Acomodo,
          QuestionAnswerTypes.NA,
        ].includes(a.id)
      );
    },
  },
  methods: {
    selectAnswer(answer) {
      if (this.isReadOnly) return;

      let updated = {
        ...this.model,
      };

      if ([QuestionAnswerTypes.NA].includes(answer.id)) {
        updated.selectedSupportAnswerId = answer.id;
        updated.selectedMainAnswerId = null;
      } else if (
        [QuestionAnswerTypes.Si, QuestionAnswerTypes.No].includes(answer.id)
      ) {
        updated.selectedMainAnswerId = answer.id;

        if (updated.selectedSupportAnswerId === QuestionAnswerTypes.NA) {
          updated.selectedSupportAnswerId = null;
        }
      } else if (
        [
          QuestionAnswerTypes.ConApoyo,
          QuestionAnswerTypes.SinApoyo,
          QuestionAnswerTypes.Acomodo,
        ].includes(answer.id)
      ) {
        updated.selectedSupportAnswerId = answer.id;
      }

      this.$emit('input', updated);
    },
    updateObservation(value) {
      this.$emit('input', {
        selectedMainAnswerId: this.model.selectedMainAnswerId,
        selectedSupportAnswerId: this.model.selectedSupportAnswerId,
        observation: value,
      });
    },
  },
};
</script>

<style scoped>
.question-row {
  padding: 1rem 0;
  border-bottom: 1px solid #e8e8e8;
}

.question-row__title {
  font-weight: 600;
  display: flex;
  align-items: center;
  min-height: 56px;
  margin-bottom: 0.75rem;
}

.question-row__observation-label {
  font-size: 0.875rem;
  margin-bottom: 0.35rem;
  color: #222;
}

.question-row__observation {
  resize: vertical;
}

.answer-card {
  background: #f5f5f5;
  border-radius: 4px 4px 0 0;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: 0.15s ease-in-out;
  cursor: pointer;
  user-select: none;
}

.answer-card--readonly {
  cursor: default;
}

.answer-card__radio {
  margin-bottom: 0.2rem;
}

.answer-card__radio-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #aab7b1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
}

.answer-card__radio-circle--selected {
  border-color: #2db782;
  background: #2db782;
  border: 2px solid #ffffff;
}

.answer-card__radio-circle-disabled--selected {
  border-color: #3f5b60;
  background: #3f5b60;
  border: 2px solid #ffffff;
}

.answer-card__check {
  font-size: 9px;
  color: #ffffff;
}

.answer-card__label {
  font-size: 0.75rem;
  text-align: center;
  color: #7b8a8d;
  line-height: 1.2;
}

.answer-card--selected .answer-card__label {
  color: #ffffff;
}

.answer-card--selected {
  background: #2db782;
}
.answer-card--disabled-selected {
  background: #3f5b60;
}
.answer-card--disabled-selected .answer-card__label {
  color: #ffffff;
}
.answer-col {
  position: relative;
}

.answer-col--with-divider::after {
  content: '';
  position: absolute;
  top: 6px;
  bottom: 6px;
  right: -1px;
  width: 1px;
  background: #d9d9d9;
}
.error-message {
  color: #ff5b5c;
}
</style>
